import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

function downloadJSON(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function flattenToCSV(obj, prefix = "") {
  const rows = [];
  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v === null || v === undefined) {
      rows.push([key, ""]);
    } else if (typeof v === "object" && !Array.isArray(v)) {
      rows.push(...flattenToCSV(v, key));
    } else if (Array.isArray(v)) {
      rows.push([key, `[${v.length} itens]`]);
    } else {
      rows.push([key, String(v)]);
    }
  }
  return rows;
}

function downloadCSV(filename, dossie) {
  const rows = flattenToCSV(dossie);
  const csv = ["campo;valor", ...rows.map(([k, v]) => `"${k}";"${String(v).replace(/"/g, '""')}"`)].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AuditoriaJuridica() {
  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="label-cap">MontaJá · Auditoria</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Auditoria Jurídica Operacional</h1>
        <p className="text-xs text-muted-foreground mt-2 max-w-3xl">
          Histórico imutável de mudanças (antes/depois/quem/quando), log de exclusões, e geração de dossiês jurídicos por entidade
          demonstrando ausência de subordinação, pessoalidade, habitualidade e onerosidade.
        </p>
      </div>

      <Tabs defaultValue="dossies">
        <TabsList className="rounded-none">
          <TabsTrigger value="dossies" data-testid="aj-tab-dossies">Dossiês Jurídicos</TabsTrigger>
          <TabsTrigger value="mudancas" data-testid="aj-tab-mudancas">Histórico de Mudanças</TabsTrigger>
          <TabsTrigger value="exclusoes" data-testid="aj-tab-exclusoes">Log de Exclusões</TabsTrigger>
        </TabsList>

        <TabsContent value="dossies" className="mt-4">
          <Dossies />
        </TabsContent>
        <TabsContent value="mudancas" className="mt-4">
          <HistoricoMudancas />
        </TabsContent>
        <TabsContent value="exclusoes" className="mt-4">
          <HistoricoExclusoes />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Dossies() {
  const [tipo, setTipo] = useState("montador");
  const [entityId, setEntityId] = useState("");
  const [dossie, setDossie] = useState(null);
  const [loading, setLoading] = useState(false);

  const gerar = async () => {
    if (!entityId) { toast.error("Informe o ID da entidade"); return; }
    setLoading(true);
    try {
      const r = await api.get(`/admin/auditoria-juridica/dossie-${tipo}/${entityId}`);
      setDossie(r.data);
      toast.success("Dossiê gerado");
    } catch (e) {
      toast.error(formatApiError(e));
      setDossie(null);
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="bg-white border border-border p-5 mb-4">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="label-cap">Tipo de dossiê</Label>
            <div className="mt-2 grid grid-cols-4 gap-1">
              {["montador", "montadora", "empresa", "atendimento"].map(t => (
                <button key={t} onClick={() => setTipo(t)}
                  className={`btn-sharp p-2 text-xs border capitalize ${tipo === t ? "brand-bg text-white border-transparent" : "border-border bg-white hover:bg-secondary"}`}
                  data-testid={`aj-tipo-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="label-cap">ID da entidade</Label>
            <Input value={entityId} onChange={(e) => setEntityId(e.target.value)}
              placeholder="UUID do montador/montadora/empresa/atendimento"
              className="mt-2 font-mono text-xs" data-testid="aj-entity-id" />
          </div>
          <Button onClick={gerar} disabled={loading} className="btn-sharp brand-bg text-white"
            data-testid="aj-gerar">
            {loading ? "Gerando…" : "Gerar Dossiê"}
          </Button>
        </div>
      </div>

      {dossie && (
        <div className="bg-white border border-border p-5 space-y-4" data-testid="aj-dossie-view">
          <div className="flex justify-between items-start border-b border-border pb-3">
            <div>
              <div className="label-cap">Dossiê {tipo}</div>
              <div className="font-heading font-extrabold text-xl mt-1">
                {dossie.montador?.nome || dossie.montadora?.nome || dossie.empresa?.nome || dossie.atendimento?.cliente_nome || "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Gerado em {fmtDate(dossie.gerado_em)} por {dossie.gerado_por_email}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="btn-sharp text-xs"
                onClick={() => downloadCSV(`dossie-${tipo}-${entityId.slice(0,8)}.csv`, dossie)}
                data-testid="aj-csv">Exportar CSV</Button>
              <Button variant="outline" size="sm" className="btn-sharp text-xs"
                onClick={() => downloadJSON(`dossie-${tipo}-${entityId.slice(0,8)}.json`, dossie)}
                data-testid="aj-json">Exportar JSON</Button>
            </div>
          </div>

          {dossie.evidencias_juridicas && (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(dossie.evidencias_juridicas).map(([categoria, items]) => (
                <div key={categoria} className="border border-border p-4 bg-[#F4F4F5]" data-testid={`aj-ev-${categoria}`}>
                  <div className="label-cap mb-2">{categoria.replace(/_/g, " ")}</div>
                  <ul className="text-xs space-y-1">
                    {Object.entries(items).map(([k, v]) => (
                      <li key={k} className="flex justify-between gap-2 border-b border-border/50 pb-1">
                        <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                        <span className="font-mono text-right max-w-[60%] truncate">{formatVal(v)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {dossie.atendimentos && (
            <Section title="Atendimentos">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                {Object.entries(dossie.atendimentos).filter(([k]) => k.startsWith("total_")).map(([k, v]) => (
                  <div key={k} className="border border-border p-3 bg-white text-center">
                    <div className="label-cap text-[9px]">{k.replace("total_", "").replace(/_/g, " ")}</div>
                    <div className="font-heading font-extrabold text-2xl mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {dossie.contrato_assinado && (
            <Section title="Contrato assinado">
              <div className="text-xs grid md:grid-cols-2 gap-3">
                <KV label="Versão" v={dossie.contrato_assinado.versao || "—"} />
                <KV label="Assinado em" v={fmtDate(dossie.contrato_assinado.timestamp)} />
                <KV label="IP" v={dossie.contrato_assinado.ip} />
                <KV label="Dispositivo" v={dossie.contrato_assinado.dispositivo} />
              </div>
            </Section>
          )}

          {dossie.historico_alteracoes_cadastro?.length > 0 && (
            <Section title={`Histórico de alterações do cadastro (${dossie.historico_alteracoes_cadastro.length})`}>
              <div className="space-y-2 text-xs max-h-64 overflow-y-auto">
                {dossie.historico_alteracoes_cadastro.map(h => (
                  <div key={h.id} className="border border-border p-2 bg-white">
                    <div className="text-muted-foreground">{fmtDate(h.em)} · {h.alterado_por_email} · IP {h.ip}</div>
                    {h.mudancas?.map((m, i) => (
                      <div key={i} className="mt-1 font-mono">
                        <strong>{m.campo}</strong>: <span className="text-[#E60000]">{String(m.valor_antes)}</span> → <span className="text-[#00A859]">{String(m.valor_depois)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </>
  );
}

function HistoricoMudancas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ entidade_tipo: "", entidade_id: "" });

  const buscar = async () => {
    setLoading(true);
    try {
      const params = Object.entries(filtros).filter(([_, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const r = await api.get(`/admin/auditoria-juridica/historico-mudancas${params ? "?" + params : ""}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { buscar(); }, []);  // eslint-disable-line

  return (
    <>
      <div className="bg-white border border-border p-4 mb-4 grid md:grid-cols-4 gap-3 items-end">
        <FF label="Tipo entidade" v={filtros.entidade_tipo} on={(v) => setFiltros(f => ({...f, entidade_tipo: v}))} testid="hm-tipo" placeholder="montadores / empresas / servicos" />
        <FF label="ID entidade" v={filtros.entidade_id} on={(v) => setFiltros(f => ({...f, entidade_id: v}))} testid="hm-id" />
        <Button onClick={buscar} disabled={loading} className="btn-sharp brand-bg text-white" data-testid="hm-buscar">Buscar</Button>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <Th>Quando</Th><Th>Entidade</Th><Th>Quem</Th><Th>IP</Th><Th>Mudanças</Th>
            </tr>
          </thead>
          <tbody>
            {!data?.mudancas?.length ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhuma mudança registrada.</td></tr>
            ) : data.mudancas.map(m => (
              <tr key={m.id} className="border-t border-border" data-testid={`hm-row-${m.id}`}>
                <Td className="text-xs">{fmtDate(m.em)}</Td>
                <Td className="font-mono text-xs">{m.entidade_tipo}<br /><span className="text-muted-foreground">{m.entidade_id?.slice(0,8)}</span></Td>
                <Td className="text-xs">{m.alterado_por_email}<br /><span className="text-muted-foreground">{m.alterado_por_role}</span></Td>
                <Td className="font-mono text-xs">{m.ip || "—"}</Td>
                <Td>
                  {m.mudancas?.map((c, i) => (
                    <div key={i} className="text-xs font-mono">
                      <strong>{c.campo}</strong>: <span className="text-[#E60000]">{String(c.valor_antes)}</span> → <span className="text-[#00A859]">{String(c.valor_depois)}</span>
                    </div>
                  ))}
                  {m.motivo && <div className="text-[10px] text-muted-foreground mt-1">Motivo: {m.motivo}</div>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HistoricoExclusoes() {
  const [data, setData] = useState(null);
  React.useEffect(() => {
    api.get("/admin/auditoria-juridica/historico-exclusoes").then(r => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-white border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-left">
          <tr><Th>Quando</Th><Th>Tipo</Th><Th>ID</Th><Th>Quem deletou</Th><Th>Motivo</Th></tr>
        </thead>
        <tbody>
          {!data?.exclusoes?.length ? (
            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhuma exclusão registrada. Soft-delete ativo.</td></tr>
          ) : data.exclusoes.map(e => (
            <tr key={e.id} className="border-t border-border" data-testid={`he-row-${e.id}`}>
              <Td className="text-xs">{fmtDate(e.em)}</Td>
              <Td className="font-mono text-xs">{e.entidade_tipo}</Td>
              <Td className="font-mono text-xs">{e.entidade_id?.slice(0,8)}</Td>
              <Td className="text-xs">{e.deletado_por_email}</Td>
              <Td className="text-xs">{e.motivo || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatVal(v) {
  if (Array.isArray(v)) return v.length === 0 ? "—" : (v.length <= 3 ? v.join(", ") : `${v.length} itens`);
  if (typeof v === "boolean") return v ? "✓ Sim" : "✗ Não";
  return String(v ?? "—");
}

function Section({ title, children }) {
  return (
    <div className="border-t border-border pt-3">
      <div className="label-cap mb-3">{title}</div>
      {children}
    </div>
  );
}
function KV({ label, v }) {
  return (
    <div>
      <div className="label-cap text-[9px]">{label}</div>
      <div className="text-sm break-all">{v || "—"}</div>
    </div>
  );
}
function FF({ label, v, on, testid, placeholder }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="mt-2 font-mono text-xs" data-testid={testid} />
    </div>
  );
}
function Th({ children }) { return <th className="p-3 label-cap text-[10px]">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
